/**
 * input.ts — captures DOM mouse/keyboard from the viewer surface and emits
 * SPEC §9.1 InputEvents on the `input` data channel.
 *
 * - Keys: HID usage primary + Unicode fallback (keymap.ts).
 * - Mouse: normalized 0..1 coords + monitor id (§9.1).
 * - Scroll: dx/dy deltas.
 * - View-only: a `{t:"viewonly", on:true}` is sent AND all input is suppressed
 *   locally; the agent + broker also enforce (SPEC §9.1 / §11.3) — we never
 *   trust the client to be the only gate.
 *
 * Pointer Lock (control mode): once the surface is locked the browser stops
 * reporting absolute clientX/Y and instead gives relative movementX/Y. We keep
 * an accumulated absolute position in surface-space so the wire stays the SPEC
 * §9.1 *normalized absolute* form (0..1) — the agent never needs to know we used
 * relative deltas. Lock is what lets the remote cursor reach screen edges and
 * frees the local cursor from the viewport.
 *
 * Payloads may be E2EE-sealed by the caller before send (sealFn). The agent
 * decrypts; the relay sees ciphertext only (SPEC §10/§11).
 */

import type { InputEvent } from '@/types';
import { MOUSE_BUTTON } from '@/types';
import { isModifierCode, modsFromEvent, resolveKey } from '@/lib/keymap';

export interface InputControllerOptions {
  channel: RTCDataChannel;
  surface: HTMLElement; // the <video> (or its wrapper) we capture over
  monitor: () => number; // current selected monitor id
  viewOnly: () => boolean;
  /** Optional E2EE seal. Returns sealed bytes (ArrayBuffer) or the raw JSON. */
  sealFn?: (json: string) => ArrayBuffer | string | null;
  /** Use Pointer Lock for control (relative mouse). Default true. */
  pointerLock?: boolean;
}

export class InputController {
  private bound = false;
  private readonly handlers: Array<{
    target: HTMLElement | Document;
    type: string;
    listener: EventListener;
  }> = [];

  // Accumulated absolute pointer position (surface pixels) while pointer-locked.
  private absX = 0;
  private absY = 0;
  private locked = false;

  constructor(private readonly opts: InputControllerOptions) {}

  attach(): void {
    if (this.bound) return;
    this.bound = true;
    const s = this.opts.surface;

    // Seed the accumulated position at the surface centre.
    const rect = s.getBoundingClientRect();
    this.absX = rect.width / 2;
    this.absY = rect.height / 2;

    this.add(s, 'mousemove', (e) => this.onMouse(e as MouseEvent, false, -1));
    this.add(s, 'mousedown', (e) => this.onMouse(e as MouseEvent, true, (e as MouseEvent).button));
    this.add(s, 'mouseup', (e) => this.onMouse(e as MouseEvent, false, (e as MouseEvent).button));
    this.add(s, 'contextmenu', (e) => e.preventDefault());
    this.add(s, 'wheel', (e) => this.onWheel(e as WheelEvent), false);

    // Pointer Lock: request on click; track lock state to switch coord modes.
    if (this.usePointerLock()) {
      this.add(s, 'click', () => this.requestLock());
      this.add(document, 'pointerlockchange', () => this.onLockChange());
    }

    // Keyboard is captured at the document level while the surface has focus.
    this.add(document, 'keydown', (e) => this.onKey(e as KeyboardEvent, true));
    this.add(document, 'keyup', (e) => this.onKey(e as KeyboardEvent, false));
  }

  private usePointerLock(): boolean {
    return (
      (this.opts.pointerLock ?? true) &&
      typeof this.opts.surface.requestPointerLock === 'function'
    );
  }

  /** Request pointer lock on the surface (control mode only, not view-only). */
  private requestLock(): void {
    if (this.opts.viewOnly()) return;
    try {
      const p = this.opts.surface.requestPointerLock();
      // Chromium returns a Promise; Safari/Firefox return void.
      (p as Promise<void> | undefined)?.catch?.(() => {});
    } catch {
      /* unsupported / user gesture required */
    }
  }

  /** Release pointer lock if we hold it. */
  exitLock(): void {
    if (document.pointerLockElement === this.opts.surface) {
      try {
        document.exitPointerLock();
      } catch {
        /* ignore */
      }
    }
  }

  private onLockChange(): void {
    this.locked = document.pointerLockElement === this.opts.surface;
    if (this.locked) {
      // Re-seed to current centre so the first delta is relative to a sane base.
      const rect = this.opts.surface.getBoundingClientRect();
      this.absX = clamp(this.absX, 0, rect.width);
      this.absY = clamp(this.absY, 0, rect.height);
    }
  }

  private add(
    target: HTMLElement | Document,
    type: string,
    fn: (e: Event) => void,
    passive = true,
  ): void {
    const listener: EventListener = (e) => fn(e);
    target.addEventListener(type, listener, { passive });
    this.handlers.push({ target, type, listener });
  }

  private send(ev: InputEvent): void {
    if (this.opts.channel.readyState !== 'open') return;
    const json = JSON.stringify(ev);
    const payload = this.opts.sealFn ? this.opts.sealFn(json) : json;
    // sealFn returns null when E2EE is not yet established: DROP the event rather
    // than send keystrokes/clicks to the relay in cleartext (the agent drops
    // pre-handshake input anyway, so nothing is lost).
    if (payload == null) return;
    // Split the ArrayBuffer|string union so each concrete type matches an
    // RTCDataChannel.send overload. The binary value is wrapped in a Uint8Array
    // VIEW rather than passed as a bare ArrayBuffer: across lib.dom.d.ts versions
    // the binary overload is `send(data: ArrayBufferView<ArrayBuffer>)` (an
    // ArrayBuffer is NOT a view), and tsc -b (project-reference mode) trips on it.
    // A view over the whole buffer is byte-identical on the wire — no runtime change.
    if (typeof payload === 'string') this.opts.channel.send(payload);
    else this.opts.channel.send(new Uint8Array(payload));
  }

  private onMouse(e: MouseEvent, down: boolean, button: number): void {
    if (this.opts.viewOnly()) return;
    const rect = this.opts.surface.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    let x: number;
    let y: number;
    if (this.locked) {
      // Pointer-locked: accumulate relative deltas into a surface-space position.
      this.absX = clamp(this.absX + e.movementX, 0, rect.width);
      this.absY = clamp(this.absY + e.movementY, 0, rect.height);
      x = clamp01(this.absX / rect.width);
      y = clamp01(this.absY / rect.height);
    } else {
      x = clamp01((e.clientX - rect.left) / rect.width);
      y = clamp01((e.clientY - rect.top) / rect.height);
      this.absX = x * rect.width;
      this.absY = y * rect.height;
    }

    this.send({
      t: 'mouse',
      x,
      y,
      monitor: this.opts.monitor(),
      buttons: e.buttons,
      down,
      button: down ? button : button === -1 ? MOUSE_BUTTON.left : button,
    });
  }

  private onWheel(e: WheelEvent): void {
    if (this.opts.viewOnly()) return;
    e.preventDefault();
    this.send({ t: 'scroll', dx: e.deltaX, dy: e.deltaY, monitor: this.opts.monitor() });
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (this.opts.viewOnly()) return;
    // Prevent the browser from acting on captured chords (best-effort; the
    // Keyboard Lock API in Session.tsx handles Ctrl+W/Alt+Tab/Win on Chromium).
    e.preventDefault();
    const { usage, unicode } = resolveKey(e);
    if (usage === 0 && unicode === undefined && !isModifierCode(e.code)) return;
    this.send({
      t: 'key',
      usage,
      down,
      mods: modsFromEvent(e),
      ...(unicode !== undefined ? { unicode } : {}),
    });
  }

  /** Emit the view-only toggle (enforced agent + broker side too). */
  setViewOnly(on: boolean): void {
    this.send({ t: 'viewonly', on });
    if (on) this.exitLock();
  }

  detach(): void {
    this.exitLock();
    for (const { target, type, listener } of this.handlers) {
      target.removeEventListener(type, listener);
    }
    this.handlers.length = 0;
    this.bound = false;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
